import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Search, X } from 'lucide-react';
import { EmptyState, PageHeader, SkeletonList } from '@/components/ui';
import { SignListItem } from '@/components/SignListItem';
import { db } from '@/db/schema';
import { getSetting, SETTING_KEYS, setSetting } from '@/db/repositories/settings';
import { useCategoryNames, useSignStates } from '@/hooks/useCatalogQueries';
import { buildSearchIndex, searchSigns } from '@/services/search/SearchService';

const MAX_RECENT_SEARCHES = 6;

export function SearchPage(): React.ReactElement {
  const [query, setQuery] = useState('');
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * `useDeferredValue` no lugar de um debounce por timer: o React mantém a digitação
   * responsiva e recalcula a lista quando sobra tempo. Sem timeout arbitrário, e o
   * resultado aparece imediatamente quando a busca é barata — que é o caso aqui.
   */
  const deferredQuery = useDeferredValue(query);

  const signs = useLiveQuery(() => db.signs.toArray(), []);
  const index = useMemo(() => (signs ? buildSearchIndex(signs) : null), [signs]);

  const results = useMemo(
    () => (index ? searchSigns(index, deferredQuery) : []),
    [index, deferredQuery],
  );

  const resultIds = useMemo(() => results.map((result) => result.sign.id), [results]);
  const states = useSignStates(resultIds);
  const categoryNames = useCategoryNames();

  useEffect(() => {
    void getSetting<string[]>(SETTING_KEYS.recentSearches, []).then(setRecentSearches);
  }, []);

  // O teclado do iPhone abre sozinho: quem chega nesta tela veio para digitar.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const rememberSearch = (term: string): void => {
    const trimmed = term.trim();
    if (trimmed.length < 2) return;

    const next = [trimmed, ...recentSearches.filter((item) => item !== trimmed)].slice(
      0,
      MAX_RECENT_SEARCHES,
    );
    setRecentSearches(next);
    void setSetting(SETTING_KEYS.recentSearches, next);
  };

  const loading = signs === undefined;
  const hasQuery = deferredQuery.trim().length > 0;

  return (
    <>
      <PageHeader title="Buscar" />

      <form
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          rememberSearch(query);
          // Fecha o teclado no iOS, revelando a lista de resultados.
          inputRef.current?.blur();
        }}
      >
        <div className="flex min-h-12 items-center gap-2 rounded-xl bg-slate-800 px-4">
          <Search size={18} className="shrink-0 text-slate-400" aria-hidden />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Nome do sinal"
            aria-label="Procurar sinal pelo nome"
            enterKeyHint="search"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            /* 16px é o mínimo que evita o zoom automático do Safari ao focar. */
            className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-slate-500"
          />
          {query.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                inputRef.current?.focus();
              }}
              aria-label="Limpar busca"
              className="-mr-2 flex min-h-11 min-w-11 items-center justify-center text-slate-400"
            >
              <X size={18} aria-hidden />
            </button>
          )}
        </div>
      </form>

      {loading && <SkeletonList rows={6} />}

      {!loading && !hasQuery && recentSearches.length > 0 && (
        <>
          <h2 className="mb-2 mt-6 text-sm font-semibold uppercase tracking-wide text-slate-400">
            Buscas recentes
          </h2>
          <ul className="flex flex-wrap gap-2">
            {recentSearches.map((term) => (
              <li key={term}>
                <button
                  type="button"
                  onClick={() => setQuery(term)}
                  className="min-h-11 rounded-full bg-slate-800 px-4 text-sm active:bg-slate-700"
                >
                  {term}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {!loading && !hasQuery && recentSearches.length === 0 && (
        <EmptyState
          icon={<Search size={40} aria-hidden />}
          title="Procure um sinal"
          description="A busca ignora acentos e maiúsculas, e funciona sem internet."
        />
      )}

      {!loading && hasQuery && results.length === 0 && (
        <EmptyState
          title="Nenhum sinal encontrado"
          description={`Nada corresponde a "${deferredQuery.trim()}".`}
        />
      )}

      {!loading && hasQuery && results.length > 0 && (
        <>
          <p aria-live="polite" className="mt-4 text-sm text-slate-400">
            {results.length} {results.length === 1 ? 'resultado' : 'resultados'}
          </p>

          <ul className="flex flex-col pb-4">
            {results.map(({ sign }) => (
              <SignListItem
                key={sign.id}
                sign={sign}
                state={states?.get(sign.id)}
                highlight={deferredQuery.trim()}
                categoryName={categoryNames?.get(sign.categoryId)}
              />
            ))}
          </ul>
        </>
      )}
    </>
  );
}
